import { zodResolver } from '@hookform/resolvers/zod';
import { createClientRequest, type ClientDto, type z } from '@zenarty/shared';
import { Pencil, Plus, Power, Store } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { api } from '../api';
import { useAuth } from '../auth';
import { EntityHistory } from '../components/audit';
import {
  Avatar,
  Badge,
  Button,
  EmptyState,
  Field,
  KeyValue,
  PageHeader,
  StatusBadge,
} from '../components/kit';
import { ConfirmDialog, Dialog, Drawer } from '../components/overlays';
import { DataTable, type Column } from '../components/table';
import { formatDate, formatDateTime, timeAgo } from '../lib/format';
import { nameMap, useAction, useClients, useSites, useUsers } from '../lib/queries';

export function ClientsPage() {
  const { can } = useAuth();
  const clients = useClients();
  const sites = useSites(can('site:read'));
  const users = useUsers(can('user:read'));
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<ClientDto | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const siteCode = nameMap(sites.data?.items, (s) => s.code);
  const portalUsers = useMemo(() => {
    const m = new Map<string, number>();
    for (const u of users.data?.items ?? [])
      if (u.clientId) m.set(u.clientId, (m.get(u.clientId) ?? 0) + 1);
    return m;
  }, [users.data]);

  const columns: Column<ClientDto>[] = [
    {
      id: 'name',
      header: 'Client',
      sortValue: (c) => c.name,
      cell: (c) => (
        <div className="row" style={{ flexWrap: 'nowrap' }}>
          <Avatar name={c.name} />
          <div>
            <div className="primary-cell">{c.name}</div>
            <span className="code-pill">{c.code}</span>
          </div>
        </div>
      ),
    },
    {
      id: 'sites',
      header: 'Stored at',
      sortValue: (c) => c.siteIds.length,
      cell: (c) =>
        c.siteIds.length ? (
          <div className="row">
            {c.siteIds.map((id) => (
              <span key={id} className="tag">
                {siteCode.get(id) ?? 'Site'}
              </span>
            ))}
          </div>
        ) : (
          <span className="faint">No sites</span>
        ),
    },
    ...(can('user:read')
      ? [
          {
            id: 'portal',
            header: 'Portal users',
            align: 'right' as const,
            sortValue: (c: ClientDto) => portalUsers.get(c.id) ?? 0,
            cell: (c: ClientDto) => portalUsers.get(c.id) ?? 0,
          },
        ]
      : []),
    {
      id: 'status',
      header: 'Status',
      sortValue: (c) => c.status,
      cell: (c) => <StatusBadge status={c.status} />,
    },
    {
      id: 'created',
      header: 'Client since',
      sortValue: (c) => c.createdAt,
      cell: (c) => <span className="muted">{formatDate(c.createdAt)}</span>,
    },
  ];

  const open = clients.data?.items.find((c) => c.id === openId) ?? null;
  return (
    <>
      <PageHeader
        title="Clients"
        description="The brands whose goods you store and ship. Every item, order and invoice belongs to exactly one client."
        actions={
          can('client:manage') ? (
            <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
              Add client
            </Button>
          ) : null
        }
      />
      <DataTable
        columns={columns}
        rows={clients.data?.items ?? []}
        rowKey={(c) => c.id}
        loading={clients.isLoading}
        searchPlaceholder="Search clients"
        searchText={(c) => `${c.name} ${c.code}`}
        onRowClick={(c) => setOpenId(c.id)}
        initialSort={{ id: 'name' }}
        empty={
          <EmptyState
            icon={Store}
            title="No clients yet"
            action={
              can('client:manage') ? (
                <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
                  Add a client
                </Button>
              ) : null
            }
          >
            Add the brands you store for, then choose which of your sites hold their goods.
          </EmptyState>
        }
      />
      {creating ? <ClientDialog onClose={() => setCreating(false)} /> : null}
      {editing ? <ClientDialog client={editing} onClose={() => setEditing(null)} /> : null}
      {open ? (
        <ClientDrawer
          client={open}
          onEdit={() => setEditing(open)}
          onClose={() => setOpenId(null)}
        />
      ) : null}
    </>
  );
}

function ClientDrawer({
  client,
  onEdit,
  onClose,
}: {
  client: ClientDto;
  onEdit: () => void;
  onClose: () => void;
}) {
  const { can } = useAuth();
  const sites = useSites(can('site:read'));
  const users = useUsers(can('user:read'));
  const [confirm, setConfirm] = useState(false);
  const toggle = useAction(
    () =>
      api(`/clients/${client.id}`, {
        method: 'PATCH',
        body: { status: client.status === 'active' ? 'inactive' : 'active' },
      }),
    {
      invalidate: [['clients']],
      success: client.status === 'active' ? 'Client deactivated' : 'Client activated',
      onSuccess: () => setConfirm(false),
    },
  );
  const siteName = nameMap(sites.data?.items, (s) => `${s.name} (${s.code})`);
  const portal = (users.data?.items ?? []).filter((u) => u.clientId === client.id);
  return (
    <Drawer
      title={client.name}
      subtitle={<span className="code-pill">{client.code}</span>}
      icon={<Avatar name={client.name} size="lg" />}
      onClose={onClose}
      footer={
        can('client:manage') ? (
          <>
            <Button icon={Pencil} onClick={onEdit}>
              Edit
            </Button>
            <Button
              variant={client.status === 'active' ? 'danger' : 'default'}
              icon={Power}
              onClick={() => setConfirm(true)}
            >
              {client.status === 'active' ? 'Deactivate' : 'Activate'}
            </Button>
          </>
        ) : null
      }
    >
      <section>
        <div className="section-title">Details</div>
        <KeyValue
          items={[
            ['Status', <StatusBadge key="s" status={client.status} />],
            ['Client since', formatDateTime(client.createdAt)],
            [
              'Receiving tolerance',
              `+${client.receiptOverTolerancePct}% / −${client.receiptShortTolerancePct}%`,
            ],
            [
              'Unfilled orders',
              client.backorderPolicy === 'backorder'
                ? 'Backorder the rest'
                : client.backorderPolicy === 'ship_complete'
                  ? 'Ship complete only'
                  : 'Ship partial',
            ],
            ['Default cut-off', client.defaultCutoff],
            ['Credit hold', client.creditHold ? 'Yes — new orders held' : 'No'],
            ['Last changed', formatDateTime(client.updatedAt)],
          ]}
        />
      </section>
      <section>
        <div className="section-title">Stored at ({client.siteIds.length})</div>
        {client.siteIds.length ? (
          <div className="row">
            {client.siteIds.map((id) => (
              <Badge key={id} tone="accent">
                {siteName.get(id) ?? 'Site'}
              </Badge>
            ))}
          </div>
        ) : (
          <p className="muted">Not assigned to any site yet.</p>
        )}
      </section>
      {can('user:read') ? (
        <section>
          <div className="section-title">Portal users ({portal.length})</div>
          {portal.length ? (
            <div className="card list">
              {portal.map((u) => (
                <div key={u.id} className="list-item">
                  <Avatar name={u.name} />
                  <div className="grow">
                    <div className="title">{u.name}</div>
                    <div className="small muted">
                      {u.email} · last sign-in {timeAgo(u.lastLoginAt)}
                    </div>
                  </div>
                  <StatusBadge status={u.status} />
                </div>
              ))}
            </div>
          ) : (
            <p className="muted">No portal users yet. Add them from Users &amp; access.</p>
          )}
        </section>
      ) : null}
      {can('audit:read') ? (
        <section>
          <div className="section-title">History</div>
          <EntityHistory entityId={client.id} />
        </section>
      ) : null}
      {confirm ? (
        <ConfirmDialog
          title={
            client.status === 'active' ? `Deactivate ${client.name}?` : `Activate ${client.name}?`
          }
          confirmLabel={client.status === 'active' ? 'Deactivate' : 'Activate'}
          danger={client.status === 'active'}
          pending={toggle.isPending}
          onConfirm={() => toggle.mutate(undefined)}
          onClose={() => setConfirm(false)}
        >
          {client.status === 'active'
            ? 'Their stock and history stay; new orders and receipts are refused until reactivated.'
            : 'The client can send orders and receipts again.'}
        </ConfirmDialog>
      ) : null}
    </Drawer>
  );
}

function ClientDialog({ client, onClose }: { client?: ClientDto; onClose: () => void }) {
  const sites = useSites();
  const form = useForm<
    z.input<typeof createClientRequest>,
    unknown,
    z.output<typeof createClientRequest>
  >({
    resolver: zodResolver(createClientRequest),
    defaultValues: client
      ? {
          code: client.code,
          name: client.name,
          siteIds: client.siteIds,
          receiptOverTolerancePct: client.receiptOverTolerancePct,
          receiptShortTolerancePct: client.receiptShortTolerancePct,
          backorderPolicy: client.backorderPolicy,
          creditHold: client.creditHold,
          defaultCutoff: client.defaultCutoff,
        }
      : {
          siteIds: [],
          receiptOverTolerancePct: 2,
          receiptShortTolerancePct: 2,
          backorderPolicy: 'ship_partial',
          creditHold: false,
          defaultCutoff: '17:00',
        },
  });
  const save = useAction(
    (v: z.output<typeof createClientRequest>) =>
      client
        ? api<ClientDto>(`/clients/${client.id}`, {
            method: 'PATCH',
            body: {
              name: v.name,
              siteIds: v.siteIds,
              receiptOverTolerancePct: v.receiptOverTolerancePct,
              receiptShortTolerancePct: v.receiptShortTolerancePct,
              backorderPolicy: v.backorderPolicy,
              creditHold: v.creditHold,
              defaultCutoff: v.defaultCutoff,
            },
          })
        : api<ClientDto>('/clients', { method: 'POST', body: v }),
    {
      invalidate: [['clients']],
      success: client ? 'Client updated' : 'Client added',
      onSuccess: onClose,
    },
  );
  const e = form.formState.errors;
  return (
    <Dialog
      title={client ? `Edit ${client.name}` : 'Add a client'}
      description="A brand whose goods you store and ship."
      onClose={onClose}
      onSubmit={(ev) => void form.handleSubmit((v) => save.mutate(v))(ev)}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" loading={save.isPending}>
            {client ? 'Save changes' : 'Add client'}
          </Button>
        </>
      }
    >
      <div className="form-grid">
        <Field label="Client name" error={e.name?.message}>
          <input
            className="input"
            autoFocus
            {...form.register('name')}
            placeholder="Sunrise Foods"
          />
        </Field>
        <Field
          label="Client code"
          error={e.code?.message}
          hint="Appears on labels, EDI and invoices"
        >
          <input
            className="input mono"
            disabled={Boolean(client)}
            {...form.register('code', { setValueAs: (v: string) => v.toUpperCase() })}
            placeholder="SUNRISE"
          />
        </Field>
      </div>
      <div>
        <div className="fieldset-title">Stored at</div>
        <div className="choice-list">
          {(sites.data?.items ?? []).map((s) => (
            <label key={s.id} className="check">
              <input type="checkbox" value={s.id} {...form.register('siteIds')} /> {s.name}{' '}
              <span className="code-pill">{s.code}</span>
            </label>
          ))}
          {sites.data?.items.length === 0 ? <span className="muted">Add a site first.</span> : null}
        </div>
      </div>
      <div>
        <div className="fieldset-title">Operations</div>
        <div className="form-grid">
          <Field
            label="Receiving over tolerance (%)"
            hint="Overages within this are accepted automatically"
            error={e.receiptOverTolerancePct?.message}
          >
            <input
              className="input num"
              inputMode="numeric"
              {...form.register('receiptOverTolerancePct')}
            />
          </Field>
          <Field
            label="Receiving short tolerance (%)"
            hint="Shortages and damage within this need no approval"
            error={e.receiptShortTolerancePct?.message}
          >
            <input
              className="input num"
              inputMode="numeric"
              {...form.register('receiptShortTolerancePct')}
            />
          </Field>
          <Field label="When an order cannot be filled">
            <select className="select" {...form.register('backorderPolicy')}>
              <option value="ship_partial">Ship what is available, cancel the rest</option>
              <option value="backorder">Ship what is available, backorder the rest</option>
              <option value="ship_complete">Hold until the order can ship complete</option>
            </select>
          </Field>
          <Field label="Default cut-off" hint="Site local time" error={e.defaultCutoff?.message}>
            <input className="input" type="time" {...form.register('defaultCutoff')} />
          </Field>
          <label className="check span-2">
            <input type="checkbox" {...form.register('creditHold')} /> Credit hold — new orders go
            on hold until released
          </label>
        </div>
      </div>
    </Dialog>
  );
}
